import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Radar,
  Brain,
  Lightbulb,
  Zap,
  MessageSquare,
  BarChart3,
  CheckCircle2,
  Play,
  RotateCcw,
  AlertTriangle,
  TrendingDown,
  User,
  Clock,
  Target,
} from 'lucide-react';

interface AgentStep {
  id: number;
  phase: 'detect' | 'reason' | 'decide' | 'act' | 'update';
  title: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
  duration: number;
}

const AgenticDemo: React.FC = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const agentSteps: AgentStep[] = [
    {
      id: 0,
      phase: 'detect',
      title: 'Signal Detection',
      description: 'Dynami AI continuously monitors engagement signals across your workforce',
      icon: <Radar size={28} color="#06BCCA" />,
      details: [
        'Analyzing 847 data points from the past 7 days',
        'Cross-referencing attendance, communication, and productivity metrics',
        'Detecting anomaly: Alex Johnson shows 47% decline in engagement indicators',
        'Pattern matches historical disengagement signatures',
      ],
      duration: 3000,
    },
    {
      id: 1,
      phase: 'reason',
      title: 'Contextual Analysis',
      description: 'Understanding the "why" behind the signals through multi-factor reasoning',
      icon: <Brain size={28} color="#FF68C5" />,
      details: [
        'Correlating with recent project deadlines and workload data',
        'Analyzing communication sentiment patterns (-23% positive sentiment)',
        'Cross-referencing with team dynamics and manager interactions',
        'Factoring tenure, role complexity, and historical engagement baseline',
        'Root cause hypothesis: Workload overwhelm + insufficient recognition',
      ],
      duration: 4000,
    },
    {
      id: 2,
      phase: 'decide',
      title: 'Intervention Selection',
      description: 'Choosing the optimal action based on predicted effectiveness',
      icon: <Lightbulb size={28} color="#FFC800" />,
      details: [
        'Evaluating 12 potential intervention pathways',
        'Scoring by: employee preference, manager availability, urgency level',
        'Selected: Proactive chatbot check-in + Manager notification',
        'Predicted effectiveness: 78% engagement improvement within 2 weeks',
        'Fallback plan queued if primary intervention shows <20% response',
      ],
      duration: 3500,
    },
    {
      id: 3,
      phase: 'act',
      title: 'Autonomous Action',
      description: 'Executing the intervention through integrated communication channels',
      icon: <Zap size={28} color="#FF9C0D" />,
      details: [
        'Triggering Slack check-in conversation with Alex (empathetic tone)',
        'Preparing manager notification for Sarah Chen',
        'Scheduling follow-up touchpoint in 48 hours',
        'Logging all actions for audit trail and learning',
      ],
      duration: 3000,
    },
    {
      id: 4,
      phase: 'update',
      title: 'Dashboard Sync',
      description: 'Updating engagement insights with new data and intervention status',
      icon: <BarChart3 size={28} color="#69277F" />,
      details: [
        'Updating Alex\'s DDI: 78 → 72 (intervention in progress)',
        'Adding intervention status to employee timeline',
        'Recalculating team-level engagement metrics',
        'Generating manager talking points for upcoming 1:1',
        'Scheduling automated effectiveness check in 14 days',
      ],
      duration: 3000,
    },
  ];

  const startDemo = () => {
    setIsPlaying(true);
    setCurrentStep(0);
    setCompletedSteps([]);
  };

  const resetDemo = () => {
    setIsPlaying(false);
    setCurrentStep(-1);
    setCompletedSteps([]);
  };

  useEffect(() => {
    if (!isPlaying || currentStep < 0) return;

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, currentStep]);
      
      if (currentStep < agentSteps.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        setIsPlaying(false);
      }
    }, agentSteps[currentStep]?.duration || 3000);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep]);

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'detect': return '#06BCCA';
      case 'reason': return '#FF68C5';
      case 'decide': return '#FFC800';
      case 'act': return '#FF9C0D';
      case 'update': return '#69277F';
      default: return '#6B7280';
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.header
        style={styles.header}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
        <div style={styles.headerContent}>
          <h1 style={styles.pageTitle}>Agentic AI in Action</h1>
          <p style={styles.pageSubtitle}>
            Watch how Dynami's autonomous AI agent detects, reasons, and acts on disengagement signals
          </p>
        </div>
        <div style={styles.headerActions}>
          {!isPlaying && currentStep === -1 && (
            <motion.button
              style={styles.playButton}
              onClick={startDemo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Play size={20} />
              Start Demo
            </motion.button>
          )}
          {(isPlaying || completedSteps.length > 0) && (
            <motion.button
              style={styles.resetButton}
              onClick={resetDemo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RotateCcw size={18} />
              Reset
            </motion.button>
          )}
        </div>
      </motion.header>

      <div style={styles.content}>
        {/* Demo Scenario */}
        <motion.div
          style={styles.scenarioCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={styles.scenarioHeader}>
            <AlertTriangle size={24} color="#EF4444" />
            <h3 style={styles.scenarioTitle}>Demo Scenario</h3>
          </div>
          <div style={styles.scenarioContent}>
            <div style={styles.employeePreview}>
              <div style={styles.employeeAvatar}>AJ</div>
              <div style={styles.employeeInfo}>
                <h4 style={styles.employeeName}>Alex Johnson</h4>
                <span style={styles.employeeRole}>Senior Software Engineer • Engineering</span>
              </div>
              <div style={styles.riskBadge}>
                <TrendingDown size={16} />
                High Risk (78)
              </div>
            </div>
            <div style={styles.signalsGrid}>
              <div style={styles.signalItem}>
                <Clock size={16} color="#F59E0B" />
                <span>Response time +150%</span>
              </div>
              <div style={styles.signalItem}>
                <User size={16} color="#F59E0B" />
                <span>Meeting attendance -40%</span>
              </div>
              <div style={styles.signalItem}>
                <Target size={16} color="#F59E0B" />
                <span>Task completion -25%</span>
              </div>
              <div style={styles.signalItem}>
                <MessageSquare size={16} color="#F59E0B" />
                <span>Slack activity -60%</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Animation Area */}
        <div style={styles.animationContainer}>
          {/* Agent Flow Visualization */}
          <div style={styles.flowVisualization}>
            {agentSteps.map((step, index) => (
              <motion.div
                key={step.id}
                style={{
                  ...styles.flowStep,
                  opacity: currentStep >= index || completedSteps.includes(index) ? 1 : 0.3,
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: currentStep === index ? 1.1 : 1, 
                  opacity: currentStep >= index || completedSteps.includes(index) ? 1 : 0.3,
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  style={{
                    ...styles.flowStepIcon,
                    background: completedSteps.includes(index) 
                      ? getPhaseColor(step.phase)
                      : currentStep === index 
                        ? `${getPhaseColor(step.phase)}20`
                        : '#F3F4F6',
                    borderColor: currentStep === index ? getPhaseColor(step.phase) : 'transparent',
                  }}
                  animate={currentStep === index ? {
                    boxShadow: [
                      `0 0 0 0 ${getPhaseColor(step.phase)}40`,
                      `0 0 0 20px ${getPhaseColor(step.phase)}00`,
                    ],
                  } : {}}
                  transition={{ duration: 1, repeat: currentStep === index ? Infinity : 0 }}
                >
                  {completedSteps.includes(index) ? (
                    <CheckCircle2 size={24} color="white" />
                  ) : (
                    step.icon
                  )}
                </motion.div>
                <span style={{
                  ...styles.flowStepLabel,
                  color: currentStep >= index ? '#1F2937' : '#9CA3AF',
                  fontWeight: currentStep === index ? 600 : 400,
                }}>
                  {step.title}
                </span>
                {index < agentSteps.length - 1 && (
                  <motion.div
                    style={{
                      ...styles.flowConnector,
                      background: completedSteps.includes(index) 
                        ? `linear-gradient(90deg, ${getPhaseColor(step.phase)}, ${getPhaseColor(agentSteps[index + 1].phase)})`
                        : '#E5E7EB',
                    }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: completedSteps.includes(index) ? 1 : 0 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Current Step Details */}
          <AnimatePresence mode="wait">
            {currentStep >= 0 && (
              <motion.div
                key={currentStep}
                style={styles.stepDetails}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.3 }}
              >
                <div style={styles.stepHeader}>
                  <div style={{
                    ...styles.stepPhase,
                    background: `${getPhaseColor(agentSteps[currentStep]?.phase)}20`,
                    color: getPhaseColor(agentSteps[currentStep]?.phase),
                  }}>
                    {agentSteps[currentStep]?.phase.toUpperCase()}
                  </div>
                  <h2 style={styles.stepTitle}>{agentSteps[currentStep]?.title}</h2>
                  <p style={styles.stepDescription}>{agentSteps[currentStep]?.description}</p>
                </div>

                <div style={styles.detailsContainer}>
                  {agentSteps[currentStep]?.details.map((detail, index) => (
                    <motion.div
                      key={index}
                      style={styles.detailItem}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.3 }}
                    >
                      <motion.div
                        style={{
                          ...styles.detailDot,
                          background: getPhaseColor(agentSteps[currentStep]?.phase),
                        }}
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 0.5, delay: index * 0.3 }}
                      />
                      <span style={styles.detailText}>{detail}</span>
                      <motion.div
                        style={styles.detailProgress}
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ 
                          duration: (agentSteps[currentStep]?.duration || 3000) / 1000 / agentSteps[currentStep]?.details.length,
                          delay: index * 0.3,
                        }}
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Processing Animation */}
                <div style={styles.processingBar}>
                  <motion.div
                    style={{
                      ...styles.processingFill,
                      background: `linear-gradient(90deg, ${getPhaseColor(agentSteps[currentStep]?.phase)}, ${getPhaseColor(agentSteps[currentStep]?.phase)}80)`,
                    }}
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: (agentSteps[currentStep]?.duration || 3000) / 1000 }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Completion State */}
          {!isPlaying && completedSteps.length === agentSteps.length && (
            <motion.div
              style={styles.completionCard}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <motion.div
                style={styles.completionIcon}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              >
                <CheckCircle2 size={48} color="#10B981" />
              </motion.div>
              <h3 style={styles.completionTitle}>Autonomous Intervention Complete</h3>
              <p style={styles.completionText}>
                Dynami's AI agent has successfully detected, analyzed, and acted on the disengagement signal.
                Alex will receive a supportive check-in, and Sarah has been notified with actionable recommendations.
              </p>
              <div style={styles.completionStats}>
                <div style={styles.completionStat}>
                  <span style={styles.completionStatValue}>~16s</span>
                  <span style={styles.completionStatLabel}>Total Processing Time</span>
                </div>
                <div style={styles.completionStat}>
                  <span style={styles.completionStatValue}>5</span>
                  <span style={styles.completionStatLabel}>Agent Steps</span>
                </div>
                <div style={styles.completionStat}>
                  <span style={styles.completionStatValue}>78%</span>
                  <span style={styles.completionStatLabel}>Predicted Success</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Initial State */}
          {currentStep === -1 && !isPlaying && completedSteps.length === 0 && (
            <motion.div
              style={styles.initialState}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div style={styles.initialIcon}>
                <Brain size={60} color="#69277F" />
              </div>
              <h3 style={styles.initialTitle}>Dynami Agentic AI</h3>
              <p style={styles.initialText}>
                Watch our autonomous AI agent analyze engagement signals, reason through context,
                and take action — all in real-time. Click "Start Demo" to begin.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F8F9FC 0%, #EDE9F0 100%)',
  },
  header: {
    padding: '24px 32px',
    background: 'white',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '24px',
    flexWrap: 'wrap',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'transparent',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
  },
  headerContent: {
    flex: 1,
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  pageSubtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  playButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#F3F4F6',
    color: '#4B5563',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px',
  },
  scenarioCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  scenarioHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  scenarioTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  scenarioContent: {},
  employeePreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  employeeAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '18px',
  },
  employeeInfo: {
    flex: 1,
  },
  employeeName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 2px 0',
  },
  employeeRole: {
    fontSize: '13px',
    color: '#6B7280',
  },
  riskBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
  },
  signalsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  signalItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    background: '#FEF3C7',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#92400E',
  },
  animationContainer: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(105, 39, 127, 0.08)',
    minHeight: '500px',
  },
  flowVisualization: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '48px',
    position: 'relative',
  },
  flowStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
    zIndex: 1,
    flex: 1,
  },
  flowStepIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px solid transparent',
    transition: 'all 0.3s ease',
  },
  flowStepLabel: {
    fontSize: '13px',
    textAlign: 'center',
    maxWidth: '100px',
  },
  flowConnector: {
    position: 'absolute',
    top: '32px',
    left: 'calc(50% + 40px)',
    width: 'calc(100% - 80px)',
    height: '4px',
    borderRadius: '2px',
    transformOrigin: 'left',
    zIndex: 0,
  },
  stepDetails: {
    background: '#F9FAFB',
    borderRadius: '20px',
    padding: '32px',
  },
  stepHeader: {
    marginBottom: '24px',
  },
  stepPhase: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    marginBottom: '12px',
  },
  stepTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 8px 0',
  },
  stepDescription: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 18px',
    background: 'white',
    borderRadius: '12px',
    position: 'relative',
    overflow: 'hidden',
  },
  detailDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  detailText: {
    fontSize: '14px',
    color: '#374151',
    flex: 1,
  },
  detailProgress: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #06BCCA, #FF68C5)',
  },
  processingBar: {
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  processingFill: {
    height: '100%',
    borderRadius: '3px',
  },
  completionCard: {
    textAlign: 'center',
    padding: '48px',
  },
  completionIcon: {
    marginBottom: '20px',
  },
  completionTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '12px',
  },
  completionText: {
    fontSize: '15px',
    color: '#6B7280',
    maxWidth: '600px',
    margin: '0 auto 32px',
    lineHeight: 1.6,
  },
  completionStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '48px',
  },
  completionStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  completionStatValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#69277F',
  },
  completionStatLabel: {
    fontSize: '13px',
    color: '#6B7280',
  },
  initialState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  initialIcon: {
    width: '120px',
    height: '120px',
    borderRadius: '30px',
    background: 'linear-gradient(135deg, rgba(105, 39, 127, 0.1) 0%, rgba(255, 104, 197, 0.1) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  initialTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '12px',
  },
  initialText: {
    fontSize: '16px',
    color: '#6B7280',
    maxWidth: '500px',
    margin: '0 auto',
    lineHeight: 1.6,
  },
};

export default AgenticDemo;


